/**
 * @bos/portal3 — the documents query/mutation layer (M5), the projects analogue of
 * projects/hooks.ts but for a project's uploaded files.
 *
 *   useDocuments(projectId) → the project's documents with loading/error/reload, plus
 *     upload (a raw multipart POST — tRPC is JSON-RPC, wrong for binary), delete, and a
 *     helper to build the authorized download URL.
 *
 * list + delete go through the typed tRPC client; upload goes through `fetch` with a
 * `FormData` body and `credentials: 'include'` so the httpOnly session cookie rides along
 * (same-origin). Download is a plain same-origin URL an <a href> can point at — the cookie
 * is attached by the browser, so no token ever appears in the URL.
 */
import { useCallback, useEffect, useState } from 'react';
import { TRPCClientError } from '@trpc/client';
import type { DocumentSummary } from '@bos/portal3-server';
import { client } from '../rpc/client';

export type { DocumentSummary };

/** The authorized, same-origin download URL for a document (the cookie rides automatically). */
export function downloadUrl(id: string): string {
  return `/api/documents/${encodeURIComponent(id)}/download`;
}

/** An upload rejected by the server (wrong type / too big / etc.) with a friendly message. */
export class UploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadError';
  }
}

/** Turn a thrown error into a message safe to render (zod issues → first message). */
export function documentErrorMessage(err: unknown): string {
  if (err instanceof UploadError) return err.message;
  if (err instanceof TRPCClientError) {
    const raw: string = err.message ?? '';
    try {
      const issues = JSON.parse(raw) as Array<{ message?: string }>;
      if (Array.isArray(issues) && issues[0]?.message) return issues[0].message;
    } catch {
      /* not a zod issue array — fall through to the plain message */
    }
    return raw || 'Something went wrong. Please try again.';
  }
  return 'Could not reach the server. Please try again.';
}

export type DocumentsStatus = 'loading' | 'ready' | 'error';

export interface UseDocuments {
  readonly documents: DocumentSummary[];
  readonly status: DocumentsStatus;
  readonly error: string | null;
  reload: () => Promise<void>;
  /** Upload a file into this project. Throws `UploadError` on a validation/server error. */
  upload: (file: File) => Promise<void>;
  /** Delete a document (row + file on disk) and drop it from the list. */
  remove: (id: string) => Promise<void>;
}

export function useDocuments(projectId: string): UseDocuments {
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [status, setStatus] = useState<DocumentsStatus>('loading');
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      setDocuments(await client.documents.listByProject.query({ projectId }));
      setStatus('ready');
    } catch (err) {
      setError(documentErrorMessage(err));
      setStatus('error');
    }
  }, [projectId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const upload = useCallback(
    async (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/documents`, {
        method: 'POST',
        body: form,
        credentials: 'include', // the httpOnly session cookie rides along
      });
      if (!res.ok) {
        let message = 'Upload failed. Please try again.';
        try {
          const body = (await res.json()) as { error?: string };
          if (body?.error) message = body.error;
        } catch {
          /* non-JSON error body — keep the default message */
        }
        throw new UploadError(message);
      }
      // The row + date are assigned server-side; reload to show the authoritative list.
      await reload();
    },
    [projectId, reload],
  );

  const remove = useCallback(async (id: string) => {
    await client.documents.delete.mutate({ id });
    setDocuments((prev) => prev.filter((d) => d.id !== id));
  }, []);

  return { documents, status, error, reload, upload, remove };
}
